import { Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { UserAvatar } from '@side/pantry';
import { formatDisplayName } from '@/common/utils/formatDisplayName';
import { formatAmount } from '../helpers';
import type { ReportsDataGroup } from '../ReportsConfiguration/useReportsRole';
import type {
  ReportingSummaryResult,
  TeamDisplayMap,
  UserDisplayMap,
} from '../types';

export type SummaryResult = Pick<
  ReportingSummaryResult,
  | 'weightedAdjustedGrossCommission'
  | 'agentNetShare'
  | 'agentFeesReceived'
  | 'agentFeesPaid'
  | 'teamNetShare'
  | 'teamFeesReceived'
  | 'teamFeesPaid'
  | 'brandNetShare'
  | 'brandFeesReceived'
  | 'brandFeesPaid'
  | 'sideNetShare'
  | 'teamMemberIdentityId'
  | 'teamIdentityId'
>;

export type PayoutBreakdownTableRow = Pick<
  SummaryResult,
  | 'agentNetShare'
  | 'agentFeesReceived'
  | 'agentFeesPaid'
  | 'teamNetShare'
  | 'teamFeesReceived'
  | 'teamFeesPaid'
  | 'brandNetShare'
  | 'brandFeesReceived'
  | 'brandFeesPaid'
  | 'sideNetShare'
  | 'weightedAdjustedGrossCommission'
> & {
  id: string;
  agent: string;
  agentAvatar: string | null | undefined;
  team: string;
};

export enum PayoutBreakdownTableColumns {
  agent = 'agent',
  team = 'team',
  weightedAdjustedGrossCommission = 'weightedAdjustedGrossCommission',
  agentFeesReceived = 'agentFeesReceived',
  agentFeesPaid = 'agentFeesPaid',
  agentNetShare = 'agentNetShare',
  teamFeesReceived = 'teamFeesReceived',
  teamFeesPaid = 'teamFeesPaid',
  teamNetShare = 'teamNetShare',
  brandFeesReceived = 'brandFeesReceived',
  brandFeesPaid = 'brandFeesPaid',
  brandNetShare = 'brandNetShare',
  sideNetShare = 'sideNetShare',
}

export function mapSummaryToPayoutBreakdownRow(
  r: SummaryResult,
  displayMaps?: {
    usersById: UserDisplayMap;
    teamsById: TeamDisplayMap;
  },
): PayoutBreakdownTableRow {
  const agentId = r.teamMemberIdentityId;
  const agent = displayMaps?.usersById[agentId ?? ''];
  const agentName = agent
    ? formatDisplayName({
        firstName: agent.firstName,
        lastName: agent.lastName,
      })
    : agentId;
  const team =
    displayMaps?.teamsById[r.teamIdentityId ?? '']?.alias ??
    displayMaps?.teamsById[r.teamIdentityId ?? '']?.legalName ??
    r.teamIdentityId;
  return {
    ...r,
    id: r.teamMemberIdentityId ?? r.teamIdentityId ?? '',
    agent: agentName ?? '',
    agentAvatar: agent?.photoUrl,
    team: team ?? '',
  };
}

export const columns: {
  groupedByAgent: GridColDef<PayoutBreakdownTableRow>[];
  groupedByTeam: GridColDef<PayoutBreakdownTableRow>[];
} = {
  groupedByAgent: [
    {
      field: PayoutBreakdownTableColumns.agent,
      headerName: 'Agent',
      renderCell: (params) => (
        <Stack
          direction="row"
          sx={{ gap: 1, alignItems: 'center', height: '100%' }}
        >
          <UserAvatar
            name={params.row.agent}
            avatarUrl={params.row.agentAvatar ?? undefined}
            sx={{ width: 30, height: 30 }}
          />
          <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
            {params.value}
          </Typography>
        </Stack>
      ),
      minWidth: 178,
      flex: 1,
      hideable: false,
    },
    {
      field: PayoutBreakdownTableColumns.weightedAdjustedGrossCommission,
      headerName: 'Post-Referral GCI',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.agentNetShare,
      headerName: 'Agent Net Commission',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.teamNetShare,
      headerName: 'Team Net Commission',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.brandNetShare,
      headerName: 'Brand Net Commission',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.sideNetShare,
      headerName: 'Side Net Commission',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.agentFeesReceived,
      headerName: 'Agent Fees Received',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.agentFeesPaid,
      headerName: 'Agent Fees Paid',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.teamFeesReceived,
      headerName: 'Team Fees Received',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.teamFeesPaid,
      headerName: 'Team Fees Paid',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.brandFeesReceived,
      headerName: 'Brand Fees Received',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.brandFeesPaid,
      headerName: 'Brand Fees Paid',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
  ],
  groupedByTeam: [
    {
      field: PayoutBreakdownTableColumns.team,
      headerName: 'Team',
      minWidth: 138,
      flex: 1,
      hideable: false,
    },
    {
      field: PayoutBreakdownTableColumns.weightedAdjustedGrossCommission,
      headerName: 'Post-Referral GCI',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.agentNetShare,
      headerName: 'Agent Net Commission',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.teamNetShare,
      headerName: 'Team Net Commission',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.brandNetShare,
      headerName: 'Brand Net Commission',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.sideNetShare,
      headerName: 'Side Net Commission',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.agentFeesReceived,
      headerName: 'Agent Fees Received',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.agentFeesPaid,
      headerName: 'Agent Fees Paid',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.teamFeesReceived,
      headerName: 'Team Fees Received',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.teamFeesPaid,
      headerName: 'Team Fees Paid',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.brandFeesReceived,
      headerName: 'Brand Fees Received',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
    {
      field: PayoutBreakdownTableColumns.brandFeesPaid,
      headerName: 'Brand Fees Paid',
      valueFormatter: formatAmount,
      minWidth: 120,
      flex: 1,
    },
  ],
};

export const VISIBILITY_MODEL: Record<
  keyof Omit<PayoutBreakdownTableRow, 'agentAvatar' | 'id'>,
  boolean
> = {
  agent: true,
  weightedAdjustedGrossCommission: true,
  agentNetShare: true,
  agentFeesReceived: false,
  agentFeesPaid: false,
  teamNetShare: true,
  teamFeesReceived: false,
  teamFeesPaid: false,
  brandNetShare: true,
  brandFeesReceived: false,
  brandFeesPaid: false,
  sideNetShare: true,
  team: true,
};

export const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

// agents are not able to see this widget, we hide all columns for them
export const payoutBreakdownDataGroups: Record<
  PayoutBreakdownTableColumns,
  ReportsDataGroup
> = {
  agent: 'agent',
  team: 'team',
  weightedAdjustedGrossCommission: 'brand',
  agentFeesReceived: 'agent',
  agentFeesPaid: 'agent',
  agentNetShare: 'agent',
  teamFeesReceived: 'team',
  teamFeesPaid: 'team',
  teamNetShare: 'team',
  brandFeesReceived: 'brand',
  brandFeesPaid: 'brand',
  brandNetShare: 'brand',
  sideNetShare: 'side',
};
